<template>
  <div class="ct">
    <div class="ct__row">
      <div class="ct__search">
        <v-icon small>mdi-magnify</v-icon>
        <input
          ref="search"
          :value="filters.search"
          :placeholder="searchPlaceholder"
          @input="onSearch($event.target.value)"
          @keydown.esc="onSearch('')"
        >
        <kbd v-if="!filters.search">/</kbd>
        <button v-else type="button" @click="onSearch('')"><v-icon x-small>mdi-close</v-icon></button>
      </div>

      <slot name="before-views" />

      <div class="ct__views" role="tablist">
        <button
          v-for="v in views"
          :key="v.key"
          type="button"
          role="tab"
          :class="{ on: view === v.key }"
          :title="v.label"
          @click="$emit('update:view', v.key)"
        >
          <v-icon small>{{ v.icon }}</v-icon>
          <span v-if="!isMobile">{{ v.label }}</span>
        </button>
      </div>

      <v-spacer />
      <slot name="actions" />
    </div>

    <div class="ct__row ct__row--chips">
      <button
        v-for="q in quick"
        :key="q.key"
        type="button"
        class="ct__chip"
        :class="{ on: isOn(q) }"
        @click="toggleQuick(q)"
      >
        <v-icon x-small>{{ q.icon }}</v-icon>{{ q.label }}
      </button>

      <v-menu v-model="menu" offset-y :close-on-content-click="false" max-width="360" min-width="320">
        <template #activator="{ on }">
          <button type="button" class="ct__chip ct__chip--filter" :class="{ on: activeCount }" v-on="on">
            <v-icon x-small>mdi-filter-variant</v-icon>{{ $t('crmFilters') }}
            <span v-if="activeCount" class="ct__badge">{{ activeCount }}</span>
          </button>
        </template>
        <div class="ct__panel">
          <div class="ct__panel-title">{{ $t('crmAdvancedFilter') }}</div>
          <div class="ct__grid">
            <v-select v-if="statusItems" :value="filters.status" :items="statusItems" :label="$t('crmStatus')" multiple outlined dense hide-details small-chips @change="set('status', $event)" />
            <v-select :value="filters.owner_user_id" :items="ownerItems" :label="$t('crmOwner')" multiple outlined dense hide-details small-chips @change="set('owner_user_id', $event)" />
            <v-select :value="filters.priority" :items="priorityItems" :label="$t('crmPriority')" multiple outlined dense hide-details small-chips @change="set('priority', $event)" />
            <v-select v-if="entity === 'lead'" :value="filters.source_id" :items="sourceItems" :label="$t('crmSource')" multiple outlined dense hide-details small-chips @change="set('source_id', $event)" />
            <template v-if="entity === 'deal'">
              <div class="ct__pair">
                <v-text-field :value="filters.min_amount" :label="$t('crmMinAmount')" outlined dense hide-details dir="ltr" type="number" @change="set('min_amount', $event)" />
                <v-text-field :value="filters.max_amount" :label="$t('crmMaxAmount')" outlined dense hide-details dir="ltr" type="number" @change="set('max_amount', $event)" />
              </div>
            </template>
            <div class="ct__pair">
              <date-picker :value="filters.fromDate" format="YYYY-MM-DD" display-format="jYYYY/jMM/jDD" :locale="dateLocale" :placeholder="$t('crmFromDate')" clearable @change="set('fromDate', $event)" />
              <date-picker :value="filters.toDate" format="YYYY-MM-DD" display-format="jYYYY/jMM/jDD" :locale="dateLocale" :placeholder="$t('crmToDate')" clearable @change="set('toDate', $event)" />
            </div>
            <v-switch :input-value="filters.include_archived" inset dense hide-details :label="$t('crmIncludeArchived')" @change="set('include_archived', $event)" />
          </div>
          <div class="ct__panel-foot">
            <v-btn small text color="primary" @click="clear">{{ $t('crmClearFilters') }}</v-btn>
            <v-btn small depressed color="primary" @click="menu = false">{{ $t('crmApply') }}</v-btn>
          </div>
        </div>
      </v-menu>

      <button v-if="activeCount || filters.search" type="button" class="ct__clear" @click="clear">
        {{ $t('crmClearFilters') }}
      </button>

      <v-spacer />
      <span class="ct__total"><slot name="summary" /></span>
    </div>
  </div>
</template>

<script>
import { crm, memberOptions, PRIORITIES } from '../crmStore'

export function emptyFilters () {
  return {
    search: '',
    status: [],
    owner_user_id: [],
    priority: [],
    source_id: [],
    unassigned: false,
    without_follow_up: false,
    not_contacted: false,
    stale_days: null,
    min_amount: null,
    max_amount: null,
    fromDate: '',
    toDate: '',
    include_archived: false,
    mine: false
  }
}

export default {
  name: 'CrmToolbar',
  props: {
    entity: { type: String, default: 'deal' },
    filters: { type: Object, required: true },
    views: { type: Array, default: () => [] },
    view: String,
    statuses: { type: Array, default: null }
  },
  data: () => ({ menu: false, timer: null }),
  computed: {
    searchPlaceholder () { return this.entity === 'lead' ? this.$t('crmSearchLeads') : this.$t('crmSearchDeals') },
    dateLocale () { return this.$i18n.locale === 'en' ? 'en' : 'fa' },
    ownerItems () { return memberOptions() },
    priorityItems () { return PRIORITIES.map(p => ({ value: p, text: this.$t('crmPriority_' + p) })) },
    sourceItems () { return crm.sources.map(s => ({ value: s.id, text: s.title })) },
    statusItems () { return this.statuses ? this.statuses.map(s => ({ value: s, text: this.$t('crmStatus_' + s) })) : null },
    quick () {
      const q = [
        { key: 'mine', icon: 'mdi-account-outline', label: this.$t('crmQuickMine') },
        { key: 'unassigned', icon: 'mdi-account-question-outline', label: this.$t('crmQuickUnassigned') },
        { key: 'without_follow_up', icon: 'mdi-calendar-remove-outline', label: this.$t('crmQuickNoFollow') }
      ]
      if (this.entity === 'lead') q.push({ key: 'not_contacted', icon: 'mdi-phone-off-outline', label: this.$t('crmQuickNotContacted') })
      else q.push({ key: 'stale_days', icon: 'mdi-sleep', label: this.$t('crmQuickStale'), value: 7 })
      return q
    },
    activeCount () {
      const f = this.filters
      return ['status', 'owner_user_id', 'priority', 'source_id'].filter(k => f[k] && f[k].length).length +
        ['min_amount', 'max_amount', 'fromDate', 'toDate', 'include_archived'].filter(k => f[k]).length
    }
  },
  mounted () { window.addEventListener('keydown', this.onKey) },
  beforeDestroy () { window.removeEventListener('keydown', this.onKey) },
  methods: {
    onKey (e) {
      if (e.key !== '/' || /input|textarea|select/i.test((e.target && e.target.tagName) || '') || (e.target && e.target.isContentEditable)) return
      e.preventDefault()
      this.$refs.search && this.$refs.search.focus()
    },
    onSearch (v) {
      clearTimeout(this.timer)
      this.timer = setTimeout(() => this.set('search', v), v ? 350 : 0)
    },
    isOn (q) { return q.value ? this.filters[q.key] === q.value : !!this.filters[q.key] },
    toggleQuick (q) {
      if (q.value) this.set(q.key, this.filters[q.key] === q.value ? null : q.value)
      else this.set(q.key, !this.filters[q.key])
    },
    set (k, v) { this.$emit('change', { ...this.filters, [k]: v }) },
    clear () { this.$emit('change', emptyFilters()) }
  }
}
</script>

<style scoped>
.ct { display: flex; flex-direction: column; gap: 10px; }
.ct__row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.ct__row--chips { gap: 6px; }
.ct__search { display: flex; align-items: center; gap: 8px; height: 38px; padding: 0 12px; border-radius: 6px; background: #fff; border: 1px solid rgba(15, 23, 42, .1); width: 280px; max-width: 100%; transition: border-color .15s, box-shadow .15s; }
.ct__search:focus-within { border-color: var(--v-primary-base); box-shadow: 0 0 0 3px rgba(25, 118, 210, .12); }
.theme--dark .ct__search { background: #1e1e1e; border-color: rgba(255, 255, 255, .12); }
.ct__search input { flex: 1; min-width: 0; outline: none; font-size: .85rem; color: inherit; background: transparent; }
.ct__search kbd { font-size: .68rem; padding: 1px 6px; border-radius: 6px; background: rgba(127, 127, 127, .14); color: inherit; box-shadow: none; opacity: .7; }
.ct__views { display: inline-flex; padding: 3px; border-radius: 6px; background: rgba(15, 23, 42, .06); }
.theme--dark .ct__views { background: rgba(255, 255, 255, .07); }
.ct__views button { display: inline-flex; align-items: center; gap: 6px; height: 32px; padding: 0 12px; border-radius: 6px; font-size: .8rem; font-weight: 600; opacity: .65; transition: all .15s; }
.ct__views button.on { background: #fff; opacity: 1; color: var(--v-primary-base); box-shadow: 0 1px 3px rgba(15, 23, 42, .12); }
.ct__views button.on ::v-deep .v-icon { color: var(--v-primary-base) !important; }
.theme--dark .ct__views button.on { background: #2b2b2b; }
.ct__chip { display: inline-flex; align-items: center; gap: 5px; height: 28px; padding: 0 10px; border-radius: 6px; border: 1px solid rgba(15, 23, 42, .12); font-size: .76rem; font-weight: 500; background: #fff; transition: all .15s; }
.theme--dark .ct__chip { background: #1e1e1e; border-color: rgba(255, 255, 255, .12); }
.ct__chip.on { border-color: var(--v-primary-base); background: rgba(25, 118, 210, .09); color: var(--v-primary-base); }
.ct__chip.on ::v-deep .v-icon { color: var(--v-primary-base) !important; }
.ct__badge { min-width: 18px; height: 18px; border-radius: 50%; background: var(--v-primary-base); color: #fff; font-size: .68rem; display: inline-flex; align-items: center; justify-content: center; }
.ct__clear { font-size: .76rem; color: var(--v-primary-base); padding: 0 6px; }
.ct__total { font-size: .78rem; opacity: .65; }
.ct__panel { background: #fff; padding: 14px; border-radius: 6px; }
.theme--dark .ct__panel { background: #1e1e1e; }
.ct__panel-title { font-weight: 700; font-size: .88rem; margin-bottom: 14px; }
.ct__grid { display: flex; flex-direction: column; gap: 16px; }
.ct__pair { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.ct__panel-foot { display: flex; justify-content: space-between; margin-top: 14px; }
.ct__panel ::v-deep .v-input { height: auto !important; margin: 0; padding: 0; }
.ct__panel ::v-deep fieldset { border-radius: 6px; }
@media (max-width: 799px) {
  .ct__search { width: 100%; order: -1; }
  .ct__row--chips { flex-wrap: nowrap; overflow-x: auto; padding-bottom: 2px; }
  .ct__chip { flex-shrink: 0; }
}
</style>
